/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import {
  generateWasteRegister,
  GenerateExcel,
} from 'services/WasteRegister-services'
import { generateColumns, generateData } from 'utils/Common/TableUtils'
import ContractEntity from 'utils/__interfaces/Perimeter/ContractEntity'
import WasteRegisterNormalizer from 'normalizer/WasteRegisterNormalizer'
import { checkRangeLimit } from 'utils/Filters/filter-utils'
import Table from 'components/Tables/Table'
import SvgIcon from 'components/Icons/SvgIcon'
import Alert from 'components/Alerts/Alert'
import Separator from 'components/Separators/Separator'
import SelectedContainer from 'components/SelectedContainer'
import Button from 'components/Buttons/Button'
import Loading from 'components/Loaders/Loading'
import ButtonSquareIcon from 'components/Buttons/ButtonSquareIcon'
import PopUpInfo from 'layouts/PopUpInfo'
import MonthRangePicker from 'components/DatePickers/MonthRangePicker'
import { TABLE_DEFAULT_RESULT_PER_PAGE } from 'utils/__consts/Common/Table'
import PianoGTM from 'utils/Common/pianoAnalytics-utils'

interface Props {
  contratsData: ContractEntity[]
  checkedList: string[]
  selectedCount: number
  handleRemoveitems: (checkedList: string[]) => void
}

function WasteRegisterGenerator({
  contratsData,
  checkedList,
  selectedCount = 0,
  handleRemoveitems,
}: Props) {
  const { t } = useTranslation()
  const [nbPage, setNbPage] = useState(TABLE_DEFAULT_RESULT_PER_PAGE)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [isErrorGenerated, setIsErrorGenerated] = useState<boolean>(false)
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(new Date().getFullYear(), 0, 1)
  )
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [showDatesLimitError, setShowDatesLimitError] = useState<boolean>(false)
  // table data preparation
  const columnNames = ['name', 'address', 'siret', '']
  const columns = generateColumns(columnNames)
  const [data, setData] = useState<any[]>([])
  // popup states
  const [showModal, setShowModal] = useState(false)
  const [popupHeader, setPopupHeader] = useState('')
  const [popupBody, setPopupBody] = useState('')
  const LARGE_API_RESPONSE_THRESHOLD = 'LARGE_API_RESPONSE_THRESHOLD'

  const setDatesChange = ([startDateMonth, endDateMonth]: [
    Date | null,
    Date | null
  ]) => {
    setStartDate(startDateMonth)
    setEndDate(endDateMonth)
    setShowDatesLimitError(
      checkRangeLimit(startDateMonth, endDateMonth, '1-years')
    )
  }

  const handleRemoveAll = () => {
    handleRemoveitems(checkedList)
  }

  const handleSingleRemove = (removeId: string) => {
    handleRemoveitems([removeId])
  }

  const itemsSelectedLabel = `${
    checkedList.length <= 1
      ? t('generic.added.singular')
      : t('generic.added.plural')
  }`
  const checkBoxLabel = `${selectedCount} ${itemsSelectedLabel}`
  const generateFile = async () => {
    // get data from api :
    if (startDate !== undefined && endDate !== undefined) {
      setIsDownloading(true)
      let isSendedByEmail = false
      /** ********************* */
      // Data layers GTM
      PianoGTM('Générer un registre déchets')
      /** ********************* */
      try {
        setIsErrorGenerated(false)
        const wasteData = await generateWasteRegister(
          startDate as Date,
          endDate as Date,
          checkedList
        )

        if (
          'message' in wasteData &&
          wasteData.message === LARGE_API_RESPONSE_THRESHOLD
        ) {
          isSendedByEmail = true
          setIsDownloading(false)
          showPopup()
          await generateWasteRegister(
            startDate as Date,
            endDate as Date,
            checkedList,
            true
          )
        } else {
          const result = WasteRegisterNormalizer.normalize(wasteData)
          GenerateExcel(result, startDate as Date, endDate as Date)
          setIsDownloading(false)
        }
      } catch (err) {
        if (!isSendedByEmail) setIsErrorGenerated(true)
        setIsDownloading(false)
      }
    }
  }
  function showSelectionList() {
    if (contratsData.length === 0) {
      return (
        <SelectedContainer>
          {t('wasteRegister.emptylistMessage')}
        </SelectedContainer>
      )
    }

    return (
      <div>
        <div className="wasteRegister_filterContainer">
          <div className="wasteRegister_datePickerContainer">
            <span className="searchBar_label">{checkBoxLabel}</span>
          </div>
          <Button className="button__secondary" onClick={handleRemoveAll}>
            <SvgIcon name="minus" className="wasteRegister_buttonicon" />
            {t('wasteRegister.removeRegister')}
          </Button>
        </div>
        <Table
          type="hidden"
          columns={columns}
          data={data}
          checkedList={checkedList}
          headerVisible={false}
          pageCount={Math.ceil(data.length / nbPage)}
          manualPagination={false}
          onNbPerPageChange={setNbPage}
          autoResetPage={false}
        />
      </div>
    )
  }

  useEffect(() => {
    setData(
      generateData(
        checkedList,
        columnNames,
        contratsData.map(({ id, name, address, numeroContratSAP }) => ({
          data1: <span className="name">{name}</span>,
          data2: <span className="address">{address}</span>,
          data4: <span className="code">{numeroContratSAP}</span>,
          data5: (
            <div>
              <ButtonSquareIcon
                iconName="bin"
                onClick={() => {
                  handleSingleRemove(id)
                }}
              />
            </div>
          ),
        }))
      )
    )
  }, [checkedList])

  const showPopup = () => {
    setPopupHeader(t('wasteRegister.popup.title'))
    setPopupBody(t('wasteRegister.popup.body'))
    setShowModal(true)
  }

  return (
    <div className="wasteRegister__right">
      <div className="wasteRegister__header_selector">
        <SvgIcon name="assesstment" className="wasteRegister_buttonicon" />
        <div className="title">{t('wasteRegister.rightTitle')}</div>
      </div>
      <div className="wasteRegister_filterCalendar">
        <div className="wasteRegister_datePickerContainer">
          <div className="wasteRegister_datePickerlabel">
            <span className="searchBar_label">
              {t('wasteRegister.picker-label')}
            </span>
          </div>
          {showDatesLimitError && (
            <span className="datePicker-error">
              {t('wasteRegister.datePickerRangeLimit')}
            </span>
          )}
          <div className="wasteRegister_datePicker">
            <MonthRangePicker
              initialstartDate={startDate}
              initialendDate={endDate}
              handleDatesChange={setDatesChange}
              isOutsideRange={(date) => {
                const today = moment()
                return moment(date).isAfter(today)
              }}
            />
          </div>
        </div>
        <div>
          <div />

          <Button
            onClick={() => generateFile()}
            disabled={
              selectedCount === 0 ||
              isDownloading ||
              showDatesLimitError ||
              !startDate ||
              !endDate
            }
            className="wasteRegister_exportBtn"
            id="wasteRegister_exportBtn"
          >
            <Loading isLoading={isDownloading} inButton>
              <SvgIcon name="uploadFile" className="wasteRegister_buttonicon" />
              {t('wasteRegister.generation_btn')}
            </Loading>
          </Button>
        </div>
      </div>

      <Separator />

      {/* show data if not empty */}
      {isErrorGenerated && (
        <Alert type="alert" alertMessaeg={t('wasteRegister.generated_error')} />
      )}
      {showSelectionList()}

      <PopUpInfo
        type="info"
        title={popupHeader}
        body={popupBody}
        show={showModal}
        onHideShow={() => {
          setShowModal(false)
        }}
        onHideShowClassName="button"
        btnText={t('wasteRegister.popup.btn')}
      />
    </div>
  )
}

export default WasteRegisterGenerator
